import React from "react";
import { Collection } from "@/services/collectionsService";
import { CheckIcon, SpinnerIcon } from "@/components/icons";
import {
  StageActions,
  PlaybackControls,
  DownloadAction,
  DownloadStatus,
  MakePublicAction,
  LEADER_KEY,
} from "./actions";
import { ReviewTagsSection } from "./ReviewTagsSection";
import { MediaStage } from "@/core/types/mediaTypes";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";

type AddToCollectionStatus = "adding" | "added" | "no-collection" | null;

interface ReviewSidebarProps {
  editorMode: boolean;
  onStageChange: (stage: MediaStage) => void;
  currentStage?: MediaStage;
  collections: Collection[];
  activeCollectionId: string | null;
  onCollectionChange: (collectionId: string | null) => void;
  onAddToCollection: () => void;
  addToCollectionStatus: AddToCollectionStatus;
  isCurrentInCollection: boolean;
  downloadStatus: DownloadStatus;
  onDownload: () => void;
  showPlaybackControls?: boolean;
  onPlayPause?: () => void;
  onSkipBackward?: () => void;
  onSkipForward?: () => void;
  clientId?: string;
  mediaItem?: MediaItem | null;
  onTagsPersisted?: (
    itemId: number,
    tags: Array<{ tag: string; properties: Record<string, string> }>,
  ) => void;
}

export const ReviewSidebar: React.FC<ReviewSidebarProps> = ({
  editorMode,
  onStageChange,
  currentStage,
  collections,
  activeCollectionId,
  onCollectionChange,
  onAddToCollection,
  addToCollectionStatus,
  isCurrentInCollection,
  downloadStatus,
  onDownload,
  showPlaybackControls = false,
  onPlayPause,
  onSkipBackward,
  onSkipForward,
  clientId,
  mediaItem,
  onTagsPersisted,
}) => {
  const showMediaActions = !editorMode && clientId && mediaItem;
  return (
    <div className="w-64 shrink-0 bg-gray-900 border-l border-gray-800 p-4 overflow-y-auto">
      <div className="space-y-4">
        {!editorMode && (
          <div className="space-y-2">
            <StageActions onStageChange={onStageChange} currentStage={currentStage} />
          </div>
        )}

        {!editorMode && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-gray-300 pt-3 border-t border-gray-700">
              <span className="text-white/70">Collection</span>
            </div>

            <select
              value={activeCollectionId || ""}
              onChange={(e) => onCollectionChange(e.target.value || null)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-sm text-gray-300 text-sm focus:outline-hidden focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
            >
              <option value="">Select collection...</option>
              {collections.map((collection) => (
                <option key={collection.id} value={collection.id}>
                  {collection.name || "Unnamed"} ({collection.itemCount})
                </option>
              ))}
            </select>

            <button
              onClick={onAddToCollection}
              disabled={addToCollectionStatus === "adding"}
              className={`w-full flex items-center justify-between px-3 py-2 rounded transition-colors ${
                isCurrentInCollection
                  ? "bg-cyan-900/50 text-cyan-300"
                  : "bg-gray-800 hover:bg-gray-700 text-gray-300"
              } disabled:opacity-50`}
            >
              <CollectionButtonContent
                status={addToCollectionStatus}
                isInCollection={isCurrentInCollection}
              />
              <kbd className="px-2 py-1 bg-gray-900 rounded-sm text-white text-xs">{LEADER_KEY} C</kbd>
            </button>
          </div>
        )}

        {showPlaybackControls && onPlayPause && onSkipBackward && onSkipForward && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-gray-300 pt-3 border-t border-gray-700">
              <span className="text-white/70">Playback</span>
            </div>
            <PlaybackControls
              onPlayPause={onPlayPause}
              onSkipBackward={onSkipBackward}
              onSkipForward={onSkipForward}
            />
          </div>
        )}

        <div className="space-y-2">
          <div className="flex items-center justify-between text-gray-300 pt-3 border-t border-gray-700">
            <span className="text-white/70">Actions</span>
          </div>
          {showMediaActions && (
            <MakePublicAction clientId={clientId} mediaItem={mediaItem} />
          )}
          <DownloadAction onDownload={onDownload} status={downloadStatus} />
        </div>

        {showMediaActions && (
          <ReviewTagsSection
            clientId={clientId}
            mediaItem={mediaItem}
            onPersisted={onTagsPersisted}
          />
        )}

        <NavigationHelp />
      </div>
    </div>
  );
};

const CollectionButtonContent: React.FC<{
  status: AddToCollectionStatus;
  isInCollection: boolean;
}> = ({ status, isInCollection }) => {
  if (status === "adding") {
    return (
      <span className="flex items-center gap-2">
        <SpinnerIcon className="w-4 h-4" />
        Adding...
      </span>
    );
  }

  if (status === "added") {
    return (
      <span className="flex items-center gap-2 text-green-400">
        <CheckIcon className="w-4 h-4" />
        Added
      </span>
    );
  }

  if (status === "no-collection") {
    return <span className="text-yellow-400">Select a collection first</span>;
  }

  if (isInCollection) {
    return (
      <span className="flex items-center gap-2">
        <CheckIcon className="w-4 h-4" />
        In Collection
      </span>
    );
  }

  return <span>Add to Collection</span>;
};

const NavigationHelp: React.FC = () => (
  <div className="space-y-2 pt-3 border-t border-gray-700">
    <div className="flex items-center justify-between text-gray-300">
      <span className="text-white/70">Navigation</span>
    </div>
    <div className="flex items-center justify-between px-3 py-2 text-gray-400 text-sm">
      <span>Previous</span>
      <kbd className="px-2 py-1 bg-gray-800 rounded-sm text-xs">Left</kbd>
    </div>
    <div className="flex items-center justify-between px-3 py-2 text-gray-400 text-sm">
      <span>Next</span>
      <kbd className="px-2 py-1 bg-gray-800 rounded-sm text-xs">Right</kbd>
    </div>
    <div className="flex items-center justify-between px-3 py-2 text-gray-400 text-sm">
      <span>Close</span>
      <kbd className="px-2 py-1 bg-gray-800 rounded-sm text-xs">Esc</kbd>
    </div>
  </div>
);
